import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Info, X } from 'lucide-react';
import './Modal.css';

export type ModalType = 'info' | 'warning' | 'urgent';

export interface ModalProps {
  isOpen: boolean;
  type?: ModalType;
  title: string;
  message?: string;
  children?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  showCancel?: boolean;
}

export default function Modal({
  isOpen,
  type = 'info',
  title,
  message,
  children,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  showCancel = true,
}: ModalProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onCancel) {
        onCancel();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'urgent':
        return <AlertTriangle size={20} className="modal-icon modal-icon--urgent modal-icon--pulse" />;
      case 'warning':
        return <AlertTriangle size={20} className="modal-icon modal-icon--warning" />;
      case 'info':
      default:
        return <Info size={20} className="modal-icon modal-icon--info" />;
    }
  };

  const getModifierClass = () => {
    if (type === 'urgent') return 'modal-content--urgent';
    if (type === 'warning') return 'modal-content--warning';
    return 'modal-content--info';
  };

  return createPortal(
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content-wrapper" onClick={e => e.stopPropagation()}>
        <div className={`modal-content ${getModifierClass()}`}>
          <div className="modal-header">
            <div className="modal-icon-wrap">
              {getIcon()}
            </div>
            <div className="modal-header-text">
              <h4 className="modal-title">{title}</h4>
              {message && <p className="modal-message">{message}</p>}
            </div>
            {onCancel && (
              <button className="modal-close" onClick={onCancel} aria-label="Close modal">
                <X size={14} />
              </button>
            )}
          </div>
          
          {children && (
            <div className="modal-body">
              {children}
            </div>
          )}

          <div className="modal-footer">
            {showCancel && onCancel && (
              <button className="modal-btn modal-btn-cancel" onClick={onCancel}>
                {cancelText}
              </button>
            )}
            {onConfirm && (
              <button 
                className={`modal-btn ${type === 'urgent' ? 'modal-btn-urgent' : 'modal-btn-confirm'}`} 
                onClick={onConfirm}
              >
                {confirmText}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
